import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../components/DashboardHeader';
import Footer from '../components/Footer';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../App.css';
import client from '../api/client';
import { toast } from 'react-toastify';

function HospitalProfile() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    hospitalId: null,
    hospitalName: '',
    contactPersonName: '',
    contactPersonRole: '',
    address: '',
    pincode: '',
    mobileNumber: '',
    landlineNumber: '',
    emailId: '',
    hospitalWebsite: '',
    password: '',
    reenterPassword: ''
  });

  const [location, setLocation] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);

  // validation states (field-wise)
  const [pincodeError, setPincodeError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [contactError, setContactError] = useState('');

  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);

  const [initialContact, setInitialContact] = useState({ emailId: '', mobileNumber: '' });
  const [initialPincode, setInitialPincode] = useState('');

  // password eye
  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);

  const addressCount = formData.address.length;
  const passwordsMatch =
    formData.password === formData.reenterPassword && (formData.reenterPassword || '').length > 0;

  // ✅ If you want email editable in future, make this true later
  const isEmailEditable = false;

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await client.get('/api/hospitals/current');
      const profile = response.data?.data;

      if (profile) {
        setFormData({
          hospitalId: profile.hospitalID ?? null,
          hospitalName: profile.hospitalName || '',
          contactPersonName: profile.contactPersonName || '',
          contactPersonRole: profile.contactPersonRole || profile.ContactPersonRole || '',
          address: profile.address || '',
          pincode: profile.pincode || '',
          mobileNumber: profile.mobileNumber || '',
          landlineNumber: profile.landlineNumber || '',
          emailId: profile.emailID || '',
          hospitalWebsite: profile.website || '',
          password: '',
          reenterPassword: ''
        });

        setInitialContact({
          emailId: profile.emailID || '',
          mobileNumber: profile.mobileNumber || ''
        });

        setInitialPincode(profile.pincode || '');

        // ✅ districtName from your API response
        setLocation(profile.district?.districtName || '');
        setIdDistrict(profile.idDistrict ?? null);
      }
    } catch (error) {
      toast.error('Failed to load profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // ✅ Handlers
  // -----------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    // pincode numeric only 6
    if (name === 'pincode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, pincode: numericValue }));
      setPincodeError('');
      setLocation('');
      setIdDistrict(null);
      return;
    }
    if (name === "password" || name === "reenterPassword") {
      if (value.length > 20) {
        toast.error("Password cannot exceed 20 characters!");
        return;
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    // mobile numeric only 10
    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, mobileNumber: numericValue }));
      setMobileError('');
      setContactError('');
      return;
    }

    // landline numeric only 15
    if (name === 'landlineNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 15);
      setFormData((prev) => ({ ...prev, landlineNumber: numericValue }));
      return;
    }

    // email (only if editable in future)
    if (name === 'emailId') {
      setFormData((prev) => ({ ...prev, emailId: value }));
      setEmailError('');
      setContactError('');
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // -----------------------------
  // ✅ Pincode validation (API)
  // -----------------------------
  const validatePincode = async () => {
    let message = '';
    setPincodeError('');
    setLocation('');
    setIdDistrict(null);

    if ((formData.pincode || '').length !== 6) {
      message = 'Enter a 6-digit pincode.';
      setPincodeError(message);
      return { ok: false, message };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(
        `/api/districts/validate-pincode?pincode=${formData.pincode}`
      );
      const result = response.data?.data;

      if (result?.districtName) {
        setLocation(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        return { ok: true, message: '' };
      }

      message = 'Enter a valid pincode.';
      setPincodeError(message);
      return { ok: false, message };
    } catch {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  // -----------------------------
  // ✅ Contact validation (API)
  // field: 'mobile' | 'email' | 'both'
  // -----------------------------
  const validateContact = async (field = 'both') => {
    
    let message = '';

    if (field === 'both') setContactError('');
    if (field !== 'email') setMobileError('');
    if (field !== 'mobile') setEmailError('');

    const emailValid =
      field === 'mobile' ? true : /\S+@\S+\.\S+/.test(formData.emailId || '');
    const mobileValid =
      field === 'email' ? true : (formData.mobileNumber || '').length === 10;

    if (!emailValid || !mobileValid) {
      if (!mobileValid && field !== 'email') {
        setMobileError('Enter a valid 10-digit mobile number.');
        message = 'Enter a valid 10-digit mobile number.';
      }
      if (!emailValid && field !== 'mobile') {
        setEmailError('Enter a valid email.');
        message = message || 'Enter a valid email.';
      }
      if (field === 'both' && message) setContactError(message);
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    // ✅ Skip uniqueness check if unchanged
    const emailChanged =
      (formData.emailId || '').trim().toLowerCase() !==
      (initialContact.emailId || '').trim().toLowerCase();

    const mobileChanged =
      (formData.mobileNumber || '').trim() !== (initialContact.mobileNumber || '').trim();

    if (
      (field === 'email' && !emailChanged) ||
      (field === 'mobile' && !mobileChanged) ||
      (field === 'both' && !emailChanged && !mobileChanged)
    ) {
      return { ok: true, message: '' };
    }

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : (formData.emailId || null),
        mobileNumber: field === 'email' ? null : (formData.mobileNumber || null)
      };

      const response = await client.post('/api/users/validate-contact', payload);
      const exists = response.data?.data;

      if (exists) {
        if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
          setContactError(message);
        }
        return { ok: false, message };
      }

      return { ok: true, message: '' };
    } catch {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'mobile') setMobileError(message);
      else if (field === 'email') setEmailError(message);
      else setContactError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };

  // -----------------------------
  // ✅ Submit
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !isEditMode) return;

    // 1) Validate pincode only if changed
    let pincodeResult = { ok: true, message: '' };
    if ((formData.pincode || '') !== (initialPincode || '')) {
      pincodeResult = await validatePincode();
    } else {
      // keep existing district if unchanged and already loaded
      if (!location && formData.pincode) {
        // if location somehow empty, validate once
        pincodeResult = await validatePincode();
      }
    }

    if (!pincodeResult.ok) {
      toast.error(pincodeResult.message || 'Please enter a valid pincode.');
      return;
    }

    // 2) Validate mobile format always; uniqueness only if changed (validateContact handles skip)
    const mobileRes = await validateContact('mobile');
    if (!mobileRes.ok) {
      toast.error(mobileRes.message || 'Please enter a valid mobile number.');
      return;
    }

    // 3) Email is read-only now; validate only if you make it editable later
    if (isEmailEditable) {
      const emailRes = await validateContact('email');
      if (!emailRes.ok) {
        toast.error(emailRes.message || 'Please enter a valid email.');
        return;
      }
    }

    // 4) Password optional (only if entered)
    if (formData.password || formData.reenterPassword) {
      if (formData.password.length < 6 || formData.password.length > 20) {
        toast.error('Password must be between 6 and 20 characters!');
        return;
      }
      if (formData.password !== formData.reenterPassword) {
        toast.error('Passwords do not match!');
        return;
      }
    }

    if (!formData.hospitalId) {
      toast.error('Hospital ID missing. Please reload and try again.');
      return;
    }

    if (!formData.address.trim()) {
      toast.error('Address is required.');
      return;
    }
    if (!formData.hospitalName.trim()) {
      toast.error('Hospital name is required.');
      return;
    }
    if (!formData.contactPersonName.trim()) {
      toast.error('Contact person name is required.');
      return;
    }
    if (!location) {
      toast.error('Enter a valid pincode to fetch location.');
      return;
    }

    const payload = {
      HospitalID: formData.hospitalId,
      HospitalName: formData.hospitalName,
      Address: formData.address,
      IdDistrict: idDistrict ?? 0,
      Pincode: formData.pincode,
      ContactPersonName: formData.contactPersonName,
      ContactPersonRole: formData.contactPersonRole || null,

      // Email read-only (still send)
      EmailID: formData.emailId,

      MobileNumber: formData.mobileNumber,
      LandlineNumber: formData.landlineNumber || null,
      Website: formData.hospitalWebsite || null,
      IsSuspended: false,
      Password: formData.password ? formData.password : null
    };

    try {
      setIsSubmitting(true);
      await client.put('/api/hospitals', payload);
      toast.success('Profile updated successfully!');

      // reset "original" trackers after success
      setIsEditMode(false);
      setInitialContact({ emailId: formData.emailId, mobileNumber: formData.mobileNumber });
      setInitialPincode(formData.pincode || '');

      // clear password boxes after save
      setFormData((prev) => ({ ...prev, password: '', reenterPassword: '' }));
      setShowPassword(false);
      setShowReenterPassword(false);
    } catch (error) {
      toast.error('Profile update failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      setIsEditMode(false);
      setEmailError('');
      setMobileError('');
      setContactError('');
      setPincodeError('');
      setShowPassword(false);
      setShowReenterPassword(false);
      loadData();
    }
  };

  const handleMenuClick = (menuItem) => {
    if (menuItem === 'profile') return;
    if (menuItem === 'dashboard') navigate('/hospital-dashboard');
  };

  return (
    <div className="App">
      <DashboardHeader
        userType="hospital"
        userName={formData.hospitalName}
        onMenuClick={handleMenuClick}
      />

      <section className="login-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-12">
              <div className="login-card">
                <div className="login-header d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="login-title">Hospital Profile</h2>
                    <p className="login-subtitle">
                      {isEditMode
                        ? 'Update your profile information below.'
                        : 'View your profile information. Click "Edit Profile" to make changes.'}
                    </p>
                  </div>
                  {!isEditMode && (
                    <button
                      type="button"
                      className="btn btn-primary mt-3"
                      onClick={() => setIsEditMode(true)}
                    >
                      Edit Profile
                    </button>
                  )}
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Name of Hospital */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="hospitalName" className="form-label">
                        Name of Hospital <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="hospitalName"
                        name="hospitalName"
                        value={formData.hospitalName}
                        onChange={handleChange}
                        maxLength="60"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                    </div>

                    {/* Contact Person */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="contactPersonName" className="form-label">
                        Contact Person Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="contactPersonName"
                        name="contactPersonName"
                        value={formData.contactPersonName}
                        onChange={handleChange}
                        maxLength="60"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                    </div>

                    {/* Contact Person Role */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="contactPersonRole" className="form-label">
                        Contact Person Role
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="contactPersonRole"
                        name="contactPersonRole"
                        value={formData.contactPersonRole}
                        onChange={handleChange}
                        placeholder="Enter contact person role (max 100 characters)"
                        maxLength="100"
                        disabled={!isEditMode || isLoading}
                      />
                    </div>

                    {/* Address */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="address" className="form-label">
                        Address <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        id="address"
                        name="address"
                        rows="3"
                        value={formData.address}
                        onChange={handleChange}
                        maxLength="200"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                      <small className="form-text text-muted">
                        {addressCount}/200 characters
                      </small>
                    </div>

                    {/* Pincode */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="pincode" className="form-label">
                        Pincode <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control w-25"
                          id="pincode"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          onBlur={() => {
                            if (!isEditMode) return;
                            if ((formData.pincode || '').length === 6 && (formData.pincode || '') !== (initialPincode || '')) {
                              validatePincode();
                            }
                          }}
                          placeholder="Enter 6-digit pincode"
                          maxLength="6"
                          pattern="[0-9]{6}"
                          disabled={!isEditMode || isLoading}
                          required
                        />
                        <input
                          type="text"
                          className="form-control w-75 form-control-display-only"
                          id="location"
                          name="location"
                          value={location}
                          placeholder="Location"
                          disabled
                          readOnly
                          tabIndex={-1}
                          aria-readonly="true"
                        />
                      </div>
                      {pincodeError && <small className="form-text text-danger">{pincodeError}</small>}
                      {isValidatingPincode && <small className="form-text text-muted">Validating pincode...</small>}
                    </div>

                    {/* Mobile */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="mobileNumber" className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="mobileNumber"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        onBlur={() => {
                          if (!isEditMode) return;
                          const cur = (formData.mobileNumber || '').trim();
                          const orig = (initialContact.mobileNumber || '').trim();
                          if (cur.length === 10 && cur !== orig) validateContact('mobile');
                        }}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                        pattern="[0-9]{10}"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                      {mobileError && <small className="form-text text-danger">{mobileError}</small>}
                      {isValidatingContact && !mobileError && !emailError && (
                        <small className="form-text text-muted">Validating contact...</small>
                      )}
                    </div>

                    {/* Landline */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="landlineNumber" className="form-label">
                        Landline Number
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="landlineNumber"
                        name="landlineNumber"
                        value={formData.landlineNumber}
                        onChange={handleChange}
                        placeholder="Enter landline number"
                        maxLength="15"
                        disabled={!isEditMode || isLoading}
                      />
                    </div>

                    {/* Email (read-only) */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="emailId" className="form-label">
                        Email ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-display-only"
                        id="emailId"
                        name="emailId"
                        value={formData.emailId}
                        onChange={handleChange}
                        onBlur={() => {
                          if (!isEditMode) return;

                          const cur = (formData.emailId || '').trim().toLowerCase();
                          const orig = (initialContact.emailId || '').trim().toLowerCase();

                          // only validate if changed + looks like an email
                          const emailValid = /^\S+@\S+\.\S+$/.test(cur);
                          if (emailValid && cur !== orig) validateContact('email');
                        }}
                        placeholder="Enter email ID"
                        maxLength="50"
                        readOnly
                        disabled
                        required
                      />
                      {emailError && <small className="form-text text-danger">{emailError}</small>}
                      {contactError && <small className="form-text text-danger">{contactError}</small>}
                    </div>

                    {/* Website */}
                    <div className="col-md-12 p-2">
                      <label htmlFor="hospitalWebsite" className="form-label">
                        Hospital Website
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        id="hospitalWebsite"
                        name="hospitalWebsite"
                        value={formData.hospitalWebsite}
                        onChange={handleChange}
                        placeholder="Enter hospital website URL"
                        maxLength="60"
                        disabled={!isEditMode || isLoading}
                      />
                    </div>

                    {/* ✅ Password fields only in edit mode */}
                    {isEditMode && (
                      <>
                        <div className="col-md-6 p-2">
                          <label htmlFor="password" className="form-label">
                            New Password{' '}
                            <small className="text-muted">(Leave blank to keep current password)</small>
                          </label>
                          <div className="input-group">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="form-control"
                              id="password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder="Enter new password (6-20 characters)"
                              minLength="6"
                              maxLength="20"
                              disabled={isLoading}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setShowPassword((s) => !s)}
                              tabIndex={-1}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </div>

                        <div className="col-md-6 p-2">
                          <label htmlFor="reenterPassword" className="form-label">
                            Re-enter New Password
                          </label>
                          <div className="input-group">
                            <input
                              type={showReenterPassword ? 'text' : 'password'}
                              className={`form-control ${formData.reenterPassword && formData.password
                                  ? passwordsMatch
                                    ? 'is-valid'
                                    : 'is-invalid'
                                  : ''
                                }`}
                              id="reenterPassword"
                              name="reenterPassword"
                              value={formData.reenterPassword}
                              onChange={handleChange}
                              placeholder="Re-enter new password"
                              minLength="6"
                              maxLength="20"
                              disabled={isLoading}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setShowReenterPassword((s) => !s)}
                              tabIndex={-1}
                            >
                              {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          {formData.reenterPassword && formData.password && !passwordsMatch && (
                            <div className="invalid-feedback d-block">Passwords do not match</div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Action buttons */}
                    {isEditMode && (
                      <div className="text-center mt-4">
                        <button
                          type="submit"
                          className="btn login-submit-btn px-5 me-2"
                          disabled={isSubmitting || isLoading || isValidatingPincode || isValidatingContact}
                        >
                          {isSubmitting ? 'Updating...' : 'Update Profile'}
                        </button>
                        <button type="button" className="btn cancel-btn px-5" onClick={handleCancel}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HospitalProfile;
